import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/constants";
import { Shield, Lock, Eye, EyeOff } from "lucide-react";

const ForcePinChange = ({ user, onPinUpdated }) => {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [withdrawalPin, setWithdrawalPin] = useState("");
  const [confirmWithdrawalPin, setConfirmWithdrawalPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [showConfirmWithdrawal, setShowConfirmWithdrawal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPin.length !== 8) {
      toast.error("Login PIN must be exactly 8 digits");
      return;
    }
    
    if (!/^\d{8}$/.test(newPin)) {
      toast.error("Login PIN must contain only numbers");
      return;
    }
    
    if (newPin !== confirmPin) {
      toast.error("Login PINs do not match");
      return;
    }
    
    if (newPin === currentPin) {
      toast.error("New Login PIN must be different from current PIN");
      return;
    }

    // Withdrawal PIN validation
    if (withdrawalPin.length !== 6) {
      toast.error("Withdrawal PIN must be exactly 6 digits");
      return;
    }
    
    if (!/^\d{6}$/.test(withdrawalPin)) {
      toast.error("Withdrawal PIN must contain only numbers");
      return;
    }
    
    if (withdrawalPin !== confirmWithdrawalPin) {
      toast.error("Withdrawal PINs do not match");
      return;
    }

    // Check that withdrawal PIN is different from login PIN
    if (newPin.includes(withdrawalPin) || withdrawalPin === newPin.substring(0, 6) || withdrawalPin === newPin.substring(2, 8)) {
      toast.error("Withdrawal PIN must be completely different from Login PIN");
      return;
    }
    
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("ltg_user") || '{}')?.access_token;
      await axios.post(`${API}/user/update-pin`, {
        user_id: user.id,
        current_pin: currentPin,
        new_pin: newPin,
        withdrawal_pin: withdrawalPin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("PINs updated successfully!");
      onPinUpdated({ ...user, pin_updated: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update PIN");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card border border-primary/50 rounded-2xl p-6 max-w-md w-full my-4">
        <div className="text-center mb-4">
          <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Shield className="text-primary" size={28} />
          </div>
          <h2 className="text-xl font-bold text-primary mb-1">Security Update Required</h2>
          <p className="text-muted-foreground text-xs">
            Set your new Login PIN (8 digits) and Withdrawal PIN (6 digits)
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Current PIN */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Current PIN</label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter current PIN"
                className="input-dark w-full pr-10"
                maxLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="border-t border-border pt-3">
            <p className="text-xs text-primary font-bold mb-2">🔐 New Login PIN (8 digits)</p>
          </div>
          
          {/* New PIN */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">New 8-Digit Login PIN</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter 8-digit login PIN"
                className="input-dark w-full pr-10"
                maxLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{newPin.length}/8 digits</p>
          </div>
          
          {/* Confirm PIN */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Confirm Login PIN</label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Confirm login PIN"
                className="input-dark w-full pr-10"
                maxLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirmPin && newPin !== confirmPin && (
              <p className="text-xs text-red-500 mt-1">PINs do not match</p>
            )}
            {confirmPin && newPin === confirmPin && newPin.length === 8 && (
              <p className="text-xs text-green-500 mt-1">✓ Match</p>
            )}
          </div>

          <div className="border-t border-border pt-3">
            <p className="text-xs text-orange-400 font-bold mb-2">💰 Withdrawal PIN (6 digits) - For fund withdrawals</p>
          </div>
          
          {/* Withdrawal PIN */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">6-Digit Withdrawal PIN</label>
            <div className="relative">
              <input
                type={showWithdrawal ? "text" : "password"}
                value={withdrawalPin}
                onChange={(e) => setWithdrawalPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter 6-digit withdrawal PIN"
                className="input-dark w-full pr-10"
                maxLength={6}
                required
              />
              <button
                type="button"
                onClick={() => setShowWithdrawal(!showWithdrawal)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showWithdrawal ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{withdrawalPin.length}/6 digits</p>
          </div>
          
          {/* Confirm Withdrawal PIN */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Confirm Withdrawal PIN</label>
            <div className="relative">
              <input
                type={showConfirmWithdrawal ? "text" : "password"}
                value={confirmWithdrawalPin}
                onChange={(e) => setConfirmWithdrawalPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Confirm withdrawal PIN"
                className="input-dark w-full pr-10"
                maxLength={6}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmWithdrawal(!showConfirmWithdrawal)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showConfirmWithdrawal ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirmWithdrawalPin && withdrawalPin !== confirmWithdrawalPin && (
              <p className="text-xs text-red-500 mt-1">PINs do not match</p>
            )}
            {confirmWithdrawalPin && withdrawalPin === confirmWithdrawalPin && withdrawalPin.length === 6 && (
              <p className="text-xs text-green-500 mt-1">✓ Match</p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={loading || newPin.length !== 8 || newPin !== confirmPin || withdrawalPin.length !== 6 || withdrawalPin !== confirmWithdrawalPin}
            className="w-full py-3 bg-primary text-black font-bold rounded-xl disabled:opacity-50 mt-4"
          >
            {loading ? "Updating..." : "Update PINs"}
          </button>
        </form>
        
        <p className="text-center text-xs text-red-400 mt-3">
          ⚠️ You cannot use the app until you set both PINs
        </p>
      </div>
    </div>
  );
};

export default ForcePinChange;
